import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Lock, PlayCircle, BookOpen } from "lucide-react";
import { Progress } from "@/components/ui/progress";
import { PublicShell, Loading, EmptyState, LockBadge } from "@/components/shared";
import api from "@/lib/api";

export default function Courses() {
  const navigate = useNavigate();
  const { data, isLoading } = useQuery({ queryKey: ["courses"], queryFn: async () => (await api.get("/courses")).data });

  return (
    <PublicShell>
      <section className="bg-navy grain">
        <div className="relative mx-auto max-w-7xl px-5 py-16 lg:px-8">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-emerald">CEO Academy</p>
          <h1 className="mt-3 font-heading text-4xl font-bold tracking-tight text-white sm:text-5xl">Learn the systems that scale</h1>
          <p className="mt-3 max-w-2xl text-slate-400">Structured, practical courses for real CEOs — anchored by the 51 Rules of Real Business. Included with Pro.</p>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-5 py-12 lg:px-8">
        {isLoading ? <Loading /> : (data?.length ? (
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {data.map((c) => {
              const pct = c.total ? Math.round((c.done / c.total) * 100) : 0;
              return (
                <Link key={c.id} to={`/courses/${c.slug}`} data-testid={`course-card-${c.id}`}
                  className="group flex flex-col overflow-hidden rounded-xl border border-slate-200 bg-white transition-all duration-200 hover:-translate-y-1 hover:shadow-[0_12px_40px_rgb(0,0,0,0.08)]">
                  <div className="relative aspect-video overflow-hidden bg-navy">
                    {c.cover_image && <img src={c.cover_image} alt={c.title} className="h-full w-full object-cover opacity-80 transition-transform duration-300 group-hover:scale-105" />}
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-white/90 text-navy"><PlayCircle className="h-6 w-6" /></div>
                    </div>
                    {c.locked && <div className="absolute left-3 top-3"><LockBadge tier={c.membership_requirement} /></div>}
                  </div>
                  <div className="flex flex-1 flex-col p-5">
                    <h3 className="font-heading text-lg font-semibold text-navy group-hover:text-emerald">{c.title}</h3>
                    <p className="mt-1 flex-1 text-sm text-slate-600">{c.subtitle}</p>
                    <div className="mt-3 flex items-center gap-3 text-xs font-medium text-slate-400"><BookOpen className="h-4 w-4" /> {c.module_count} modules · {c.lesson_count} lessons</div>
                    {!c.locked && c.done > 0 && (
                      <div className="mt-3"><Progress value={pct} className="h-1.5" /><p className="mt-1.5 text-xs text-slate-500">{pct}% complete</p></div>
                    )}
                  </div>
                </Link>
              );
            })}
          </div>
        ) : <EmptyState title="No courses yet" subtitle="New courses are on the way." />)}
      </section>
    </PublicShell>
  );
}
